import apiClient from './api';

export interface Ticket {
  id: string;
  reference: string;
  date_heure: string;
  statut: 'COMPLETEE' | 'REFOULEE';
  statut_display: string;
  type_pesee: string;
  type_pesee_display: string;
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  provenance: string;
  destination: string;
  numero_lot: string;
  observations: string;
  // Champs de comptage des sacs
  nb_sacs_declares?: number;
  nb_sacs_sains?: number;
  nb_sacs_recond?: number;
  nb_sacs_humides?: number;
  nb_sacs_dechires?: number;
  nb_sacs_rejetes?: number;
  nb_sacs_acceptes?: number;
  camion_immatriculation: string;
  chauffeur_nom: string;
  produit_nom: string;
  fournisseur_nom: string;
  client_nom: string;
  exportateur_nom: string;
  transporteur_nom?: string;
  convoyeur_nom?: string;
  // Champs email pour les acteurs
  client_email?: string;
  fournisseur_email?: string;
  exportateur_email?: string;
  chauffeur_email?: string;
  transporteur_email?: string;
  convoyeur_email?: string;
  camion_info: {
    id: number;
    immatriculation: string;
    marque: string;
    modele: string;
    capacite: number;
    proprietaire: string;
  } | null;
  chauffeur_info: {
    id: number;
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  } | null;
  produit_info: {
    id: number;
    nom: string;
    type: string;
    type_display: string;
    unite: string;
    categorie: string;
  } | null;
  fournisseur_info: {
    id: number;
    nom: string;
    cooperative: string;
    contact: string;
    email: string;
  } | null;
  client_info: {
    id: number;
    nom: string;
    type: string;
    telephone: string;
    email: string;
    secteur: string;
  } | null;
  exportateur_info: {
    id: number;
    nom: string;
    type: string;
    telephone: string;
    email: string;
    pays: string;
    zone: string;
  } | null;
  transporteur_info?: {
    id: number;
    nom: string;
    type: string;
    telephone: string;
    email: string;
    adresse: string;
  } | null;
  convoyeur_info?: {
    id: number;
    nom: string;
    type: string;
    telephone: string;
    email: string;
    adresse: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface TicketsResponse {
  success: boolean;
  message: string;
  data: Ticket[];
}

export interface TicketFilters {
  search?: string;
  statut?: 'COMPLETEE' | 'REFOULEE' | '';
  date_from?: string;
  date_to?: string;
}

class TicketsService {
  /**
   * Récupérer tous les tickets (pesées COMPLETEE et REFOULEE)
   */
  async getTickets(filters: TicketFilters = {}): Promise<TicketsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.statut) {
        params.append('statut', filters.statut);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }

      const response = await apiClient.get(`/weighing/api/pesees/tickets/?${params.toString()}`);
     
      // Vérifier la structure de la réponse
      if (response.data && response.data.data) {
        return response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // Structure de pagination Django REST Framework
        return {
          success: true,
          message: 'Tickets récupérés avec succès',
          data: response.data.results
        };
      } else if (Array.isArray(response.data)) {
        return {
          success: true,
          message: 'Tickets récupérés avec succès',
          data: response.data
        };
      } else {
        console.error('Structure de réponse inattendue:', response.data);
        return {
          success: false,
          message: 'Structure de réponse inattendue',
          data: []
        };
      }
    } catch (error: any) {
      console.error('Erreur lors de la récupération des tickets:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des tickets');
    }
  }

  /**
   * Récupérer un ticket par son ID
   */
  async getTicketById(id: string): Promise<Ticket> {
    try {
      const response = await apiClient.get(`/weighing/api/pesees/${id}/`);
      return response.data.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération du ticket:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération du ticket');
    }
  }

  /**
   * Imprimer un ticket
   */
  async printTicket(id: string): Promise<Blob> {
    try {
      const response = await apiClient.post(`/weighing/api/tickets/${id}/imprimer/`, {}, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de l\'impression du ticket:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'impression du ticket');
    }
  }

  /**
   * Exporter un ticket en PDF
   */
  async exportTicketPDF(id: string): Promise<Blob> {
    try {
      const response = await apiClient.post(`/weighing/api/tickets/${id}/exporter-pdf/`, {}, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de l\'export PDF du ticket:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'export PDF du ticket');
    }
  }

  /**
   * Envoyer un ticket par email
   */
  async sendTicketEmail(id: string, email: string): Promise<any> {
    try {
      const response = await apiClient.post(`/weighing/api/tickets/${id}/envoyer-email/`, {
        email: email
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi par email du ticket:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'envoi par email du ticket');
    }
  }

  /**
   * Modifier les observations d'un ticket refoulé
   */
  async updateTicketObservations(id: string, observations: string): Promise<Ticket> {
    try {
      const response = await apiClient.patch(`/weighing/api/pesees/${id}/`, {
        observations: observations
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour des observations:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour des observations');
    }
  }
}

export default new TicketsService();
